import React, { useState, useEffect } from 'react';
import { useProjectMerge } from '../../hooks/useProjectMerge';
import { Button } from '../ui/Button/Button';
import { MERGE_STEPS } from '../../types/merge';
import type { MergeStep } from '../../types/merge';
import { ProjectSelection } from './ProjectSelection';
import { TargetsMerge } from './TargetsMerge';
import { AspectsMerge } from './AspectsMerge';
import { RubricMerge } from './RubricMerge';
import { EvaluationsMerge } from './EvaluationsMerge';
import { GradesMerge } from './GradesMerge';

interface ProjectMergeWizardProps {
  onComplete: () => void;
  onCancel: () => void;
}

export const ProjectMergeWizard: React.FC<ProjectMergeWizardProps> = ({
  onComplete,
  onCancel
}) => {
  const [selectedProjects, setSelectedProjects] = useState<[string, string] | null>(null);
  const [selectedTitles, setSelectedTitles] = useState<[string, string] | null>(null);
  const [showWorkspaceList, setShowWorkspaceList] = useState(true);

  const {
    currentWorkspace,
    availableWorkspaces,
    isLoading,
    error,
    isRestoring,
    startNewMerge,
    loadWorkspace,
    deleteWorkspace,
    updateStepData,
    finalizeProject,
    clearError
  } = useProjectMerge({
    onStepComplete: (step, data) => {
      console.log(`Step ${step} completed:`, data);
    },
    onError: (error) => {
      console.error('Merge error:', error);
    }
  });

  // ワークスペースリストの表示制御
  useEffect(() => {
    if (currentWorkspace) {
      setShowWorkspaceList(false);
      setSelectedProjects(currentWorkspace.sourceProjects);
      setSelectedTitles(currentWorkspace.sourceProjectTitles);
    } else {
      setShowWorkspaceList(true);
    }
  }, [currentWorkspace]);

  const handleProjectSelection = (projectIds: [string, string], projectTitles: [string, string]) => {
    setSelectedProjects(projectIds);
    setSelectedTitles(projectTitles);
    const workspace = startNewMerge(projectIds, projectTitles);
    setShowWorkspaceList(false);
    // startNewMergeによってcurrentWorkspaceが設定されるので、
    // useEffectで自動的にstep 1（現在のステップ）のコンポーネントが表示される
  };

  const handleLoadWorkspace = (workspaceId: string) => {
    const workspace = loadWorkspace(workspaceId);
    if (workspace) {
      setSelectedProjects(workspace.sourceProjects);
      setSelectedTitles(workspace.sourceProjectTitles);
      setShowWorkspaceList(false);
    }
  };

  const handleStepComplete = (step: MergeStep, data: any) => {
    updateStepData(step, data);
    
    // 最終ステップの場合は完了処理
    if (step === 6) {
      handleFinalize();
    }
  };

  const handleFinalize = async () => {
    if (!currentWorkspace) return;
    
    const success = await finalizeProject(currentWorkspace.finalProjectTitle);
    if (success) {
      onComplete();
    }
  };

  const handleGoBack = () => {
    setShowWorkspaceList(true);
    setSelectedProjects(null);
    setSelectedTitles(null);
  };

  const getCurrentStepComponent = () => {
    if (!currentWorkspace || !selectedProjects) return null;

    switch (currentWorkspace.currentStep) {
      case 1:
        return (
          <ProjectSelection
            onSelect={handleProjectSelection}
            selectedProjects={selectedProjects}
            onCancel={onCancel}
          />
        );
      case 2:
        return (
          <TargetsMerge
            projectIds={selectedProjects}
            onComplete={(data) => handleStepComplete(3, data)}
            onBack={handleGoBack}
            initialData={currentWorkspace.stepData.targets}
          />
        );
      case 3:
        return (
          <AspectsMerge
            projectIds={selectedProjects}
            onComplete={(data) => handleStepComplete(4, data)}
            onBack={() => updateStepData(2, currentWorkspace.stepData.targets)}
            initialData={currentWorkspace.stepData.aspects}
          />
        );
      case 4:
        return (
          <RubricMerge
            mergedAspects={currentWorkspace.stepData.aspects?.merged || []}
            onComplete={(data) => handleStepComplete(5, data)}
            onBack={() => updateStepData(3, currentWorkspace.stepData.aspects)}
            initialData={currentWorkspace.stepData.rubric}
          />
        );
      case 5:
        return (
          <EvaluationsMerge
            projectIds={selectedProjects}
            mergedTargets={currentWorkspace.stepData.targets?.merged || []}
            mergedAspects={currentWorkspace.stepData.aspects?.merged || []}
            onComplete={(data) => handleStepComplete(6, data)}
            onBack={() => updateStepData(4, currentWorkspace.stepData.rubric)}
            initialData={currentWorkspace.stepData.evaluations}
          />
        );
      case 6:
        return (
          <GradesMerge
            projectIds={selectedProjects}
            mergedAspects={currentWorkspace.stepData.aspects?.merged || []}
            onComplete={(data) => handleStepComplete(6, data)}
            onBack={() => updateStepData(5, currentWorkspace.stepData.evaluations)}
            onFinalize={handleFinalize}
            initialData={currentWorkspace.stepData.grades}
          />
        );
      default:
        return null;
    }
  };

  // ワークスペースリスト表示
  if (showWorkspaceList) {
    return (
      <div className="project-merge-wizard workspace-list-view">
        <div className="wizard-header">
          <h1>プロジェクト統合</h1>
          <p>2つの評価プロジェクトを選択して手動で統合します</p>
        </div>

        {availableWorkspaces.length > 0 && (
          <div className="saved-workspaces">
            <h2>進行中の統合作業</h2>
            <div className="workspaces-list">
              {availableWorkspaces.map(workspace => (
                <div key={workspace.id} className="workspace-card">
                  <div className="workspace-info">
                    <h3>{workspace.finalProjectTitle}</h3>
                    <p>
                      {workspace.sourceProjectTitles[0]} + {workspace.sourceProjectTitles[1]}
                    </p>
                    <div className="workspace-progress">
                      <span>ステップ {workspace.currentStep}/6</span>
                      <span>{MERGE_STEPS[workspace.currentStep - 1]?.title}</span>
                    </div>
                    <span className="workspace-date">
                      更新: {workspace.updatedAt.toLocaleDateString('ja-JP')}
                    </span>
                  </div>
                  <div className="workspace-actions">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleLoadWorkspace(workspace.id)}
                    >
                      続行
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteWorkspace(workspace.id)}
                      title="削除"
                    >
                      削除
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="new-merge-section">
          <h2>新しい統合を開始</h2>
          <ProjectSelection
            onSelect={handleProjectSelection}
            onCancel={onCancel}
          />
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
            <Button size="sm" onClick={clearError}>
              閉じる
            </Button>
          </div>
        )}

        <style>{`
          .project-merge-wizard {
            max-width: 1000px;
            margin: 0 auto;
            padding: 2rem;
            min-height: 600px;
          }

          .workspace-list-view {
            max-width: 800px;
          }

          .wizard-header {
            text-align: center;
            margin-bottom: 2rem;
          }

          .wizard-header h1 {
            font-size: 1.75rem;
            color: #1f2937;
            margin: 0 0 0.5rem 0;
          }

          .wizard-header p {
            color: #6b7280;
            margin: 0;
          }

          .saved-workspaces {
            margin-bottom: 2rem;
            padding: 1.5rem;
            background: #f9fafb;
            border-radius: 0.75rem;
          }

          .saved-workspaces h2 {
            font-size: 1.25rem;
            color: #1f2937;
            margin: 0 0 1rem 0;
          }

          .workspaces-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .workspace-card {
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: white;
            padding: 1rem;
            border-radius: 0.5rem;
            border: 1px solid #e5e7eb;
          }

          .workspace-info h3 {
            font-size: 1rem;
            font-weight: 600;
            color: #1f2937;
            margin: 0 0 0.25rem 0;
          }

          .workspace-info p {
            font-size: 0.875rem;
            color: #6b7280;
            margin: 0 0 0.5rem 0;
          }

          .workspace-progress {
            display: flex;
            gap: 0.5rem;
            font-size: 0.75rem;
            color: #3b82f6;
            margin-bottom: 0.25rem;
          }

          .workspace-date {
            font-size: 0.75rem;
            color: #9ca3af;
          }

          .workspace-actions {
            display: flex;
            gap: 0.5rem;
          }

          .new-merge-section {
            margin-bottom: 2rem;
          }

          .new-merge-section h2 {
            font-size: 1.25rem;
            color: #1f2937;
            margin: 0 0 1rem 0;
          }

          .error-message {
            background: #fee2e2;
            border: 1px solid #fecaca;
            border-radius: 0.5rem;
            padding: 1rem;
            margin-top: 1rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .error-message p {
            color: #dc2626;
            margin: 0;
          }

          @media (max-width: 768px) {
            .project-merge-wizard {
              padding: 1rem;
            }

            .workspace-card {
              flex-direction: column;
              align-items: flex-start;
              gap: 1rem;
            }

            .workspace-actions {
              align-self: stretch;
              justify-content: space-between;
            }
          }
        `}</style>
      </div>
    );
  }

  // メインのウィザード表示
  return (
    <div className="project-merge-wizard">
      <div className="wizard-header">
        <h1>プロジェクト統合</h1>
        {currentWorkspace && (
          <div className="step-indicator">
            <span>ステップ {currentWorkspace.currentStep}/6</span>
            <span>{MERGE_STEPS[currentWorkspace.currentStep - 1]?.title}</span>
          </div>
        )}
      </div>
      
      <div className="wizard-content">
        {getCurrentStepComponent()}
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <Button size="sm" onClick={clearError}>
            閉じる
          </Button>
        </div>
      )}

      <style>{`
        .step-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .step-indicator span:first-child {
          color: #3b82f6;
          font-weight: 600;
        }

        .wizard-content {
          min-height: 400px;
        }
      `}</style>
    </div>
  );
};